import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Settings, BarChart3, FileText, LogOut, Sparkles, Crown, Wand2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const navItems = [
    { path: '/app/setup', label: 'Setup', icon: Settings },
    { path: '/app/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/app/posts', label: 'Posts', icon: FileText },
    { path: '/app/settings', label: 'AI Settings', icon: Wand2 },
    { path: '/app/account', label: 'Account', icon: Crown },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-cyan-50/30">
      <nav className="bg-white/80 backdrop-blur-lg border-b border-slate-200/60 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-12">
              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-purple-600 to-cyan-500 p-2 rounded-lg">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg font-heading font-bold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent" data-testid="app-title">
                  WP Magica
                </h1>
              </div>
              <div className="hidden md:flex items-center space-x-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-link-${item.label.toLowerCase()}`}
                      className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-purple-600 to-cyan-600 text-white shadow-md'
                          : 'text-slate-600 hover:text-purple-600 hover:bg-purple-50'
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <div className="hidden sm:flex items-center gap-3">
                  {/* Plan Badge */}
                  {user.subscription?.plan === 'lifetime' ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full shadow-lg shadow-amber-500/20">
                      <Crown className="w-3.5 h-3.5 text-black" />
                      <span className="text-xs font-bold text-black uppercase tracking-wide">Lifetime</span>
                    </div>
                  ) : user.subscription?.plan === 'pro' ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-full shadow-lg shadow-purple-500/20">
                      <Crown className="w-3.5 h-3.5 text-white" />
                      <span className="text-xs font-bold text-white uppercase tracking-wide">Pro</span>
                    </div>
                  ) : (
                    <Link 
                      to="/pricing"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-purple-50 rounded-full border border-slate-200 hover:border-purple-300 transition-all"
                    >
                      <span className="text-xs font-medium text-slate-600">Free Plan</span>
                      <span className="text-xs text-purple-600 font-semibold">Upgrade</span>
                    </Link>
                  )}
                  {/* User Email */}
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-lg border border-purple-200/50">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-slate-700 font-medium">
                      {user.email}
                    </span>
                  </div>
                </div>
              )}
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className="flex items-center gap-2 border-slate-300 text-slate-700 hover:bg-red-50 hover:text-red-600 hover:border-red-300 transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Decorative bottom gradient */}
      <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 via-cyan-500 to-purple-600 opacity-50"></div>
    </div>
  );
};

export default Layout;
